import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Loader2, Download, FileText, DollarSign, ShieldCheck, Lock,
  AlertCircle, CheckCircle2, FileCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const CLASS_LABELS = {
  individual_sole_proprietor: 'Individual / Sole Proprietor',
  c_corporation: 'C Corporation',
  s_corporation: 'S Corporation',
  partnership: 'Partnership',
  trust_estate: 'Trust / Estate',
  llc: 'Limited Liability Company',
  other: 'Other',
};

const PROFILE_STATUS = {
  needed: { label: 'Tax information needed', tone: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertCircle },
  received: { label: 'Tax information received', tone: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle2 },
  under_review: { label: 'Under review', tone: 'bg-blue-100 text-blue-800 border-blue-200', icon: Loader2 },
  validated: { label: 'Tax information validated', tone: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: ShieldCheck },
  rejected: { label: 'Needs correction', tone: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
};

export default function TaxCenter() {
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (y) => {
    setLoading(true);
    try {
      const r = await api.get(`/tax-center/me/summary?year=${y}`);
      setData(r.data);
    } catch {
      toast.error('Could not load your Tax Center');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(year); }, [year, load]);

  const totals = data?.totals || {};
  const prof = data?.tax_profile || { status: 'needed' };
  const rule = data?.tax_year_rule || {};
  const years = data?.available_years || [year];
  const pstat = PROFILE_STATUS[prof.status] || PROFILE_STATUS.needed;

  const downloadStatement = async () => {
    try {
      const r = await api.get(`/tax-center/me/statement/export?year=${year}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `annual-earnings-${year}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const downloadForm = async (f) => {
    setBusy(true);
    try {
      const r = await api.get(`/tax-center/me/forms/${f.form_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `1099-NEC-${f.tax_year}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      load(year);
    } catch { toast.error('This form is not available for download yet'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tax-center-page">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/talent/earnings')} data-testid="tax-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Earnings
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Tax Center</h1>
              <p className="text-sm text-slate-500">Your contractor tax documents &amp; W-9</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`border ${pstat.tone}`} data-testid="tax-profile-status">{pstat.label}</Badge>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28" data-testid="tax-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="space-y-4">
            <TabsList data-testid="tax-tabs">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="w9" data-testid="tab-w9">Tax Info (W-9)</TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
              <TabsTrigger value="corrections" data-testid="tab-corrections">Corrections</TabsTrigger>
            </TabsList>

            {/* OVERVIEW */}
            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={DollarSign} label={`${year} Reportable (1099-NEC)`} value={money(totals.nec_reportable)} tone="text-emerald-600" testid="stat-reportable" />
                <StatCard icon={FileText} label="Net Paid" value={money(totals.net_paid)} testid="stat-net" />
                <StatCard icon={ShieldCheck} label="Reimbursements (non-taxable)" value={money(totals.reimbursements_non_reportable)} testid="stat-reimb" />
                <StatCard icon={FileCheck} label="Payments" value={totals.payment_count || 0} testid="stat-count" />
              </div>

              <Card>
                <CardContent className="p-4">
                  <div className={`flex items-start gap-3 rounded-lg p-3 border ${data?.requires_1099 ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                    {data?.requires_1099 ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" /> : <AlertCircle className="w-5 h-5 text-slate-400 mt-0.5" />}
                    <div className="text-sm">
                      <p className="font-medium text-slate-900" data-testid="requires-1099">
                        {data?.requires_1099
                          ? `You are expected to receive a Form 1099-NEC for ${year}.`
                          : `Based on payments so far, a 1099-NEC is not required for ${year}.`}
                      </p>
                      <p className="text-slate-600 mt-1">{rule.note}</p>
                      <p className="text-slate-500 mt-1">An annual earnings summary is always available to you regardless of the threshold.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900">Qualifying payments ({year})</h3>
                    <Button variant="outline" size="sm" onClick={downloadStatement} data-testid="download-statement-btn">
                      <Download className="w-4 h-4 mr-1" /> Earnings Statement
                    </Button>
                  </div>
                  {(data?.payments || []).length === 0 ? (
                    <p className="text-sm text-slate-500 py-6 text-center">No payments recorded for {year}.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-slate-500 border-b">
                          <th className="py-2 pr-3">Paid</th><th className="pr-3">Event</th>
                          <th className="pr-3 text-right">Gross</th><th className="pr-3 text-right">Net</th>
                          <th className="pr-3">Method</th><th className="pr-3">Form</th>
                        </tr></thead>
                        <tbody>
                          {data.payments.map((p) => (
                            <tr key={p.payout_id} className="border-b last:border-0" data-testid={`payment-row-${p.payout_id}`}>
                              <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(p.paid_at)}</td>
                              <td className="pr-3">{p.event_name}</td>
                              <td className="pr-3 text-right">{money(p.gross)}</td>
                              <td className="pr-3 text-right">{money(p.net_paid)}</td>
                              <td className="pr-3">{p.method}</td>
                              <td className="pr-3"><Badge variant="outline" className="text-xs uppercase">{p.bucket === 'nec' ? '1099-NEC' : p.bucket === 'k' ? '1099-K' : '—'}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* W-9 */}
            <TabsContent value="w9">
              <W9Form profile={prof} onSaved={() => load(year)} />
            </TabsContent>

            {/* DOCUMENTS */}
            <TabsContent value="documents">
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Tax documents</h3>
                  {(data?.forms || []).length === 0 ? (
                    <p className="text-sm text-slate-500 py-6 text-center" data-testid="no-forms">
                      No 1099-NEC has been issued yet. When your form is prepared and approved it will appear here.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {data.forms.map((f) => (
                        <div key={f.form_id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`form-row-${f.form_id}`}>
                          <div>
                            <p className="font-medium text-slate-900">Form 1099-NEC — {f.tax_year}{f.is_correction ? ' (Corrected)' : ''}</p>
                            <p className="text-xs text-slate-500">Box 1: {money(f.box1)} · {f.status_label} · IRS: {f.filing_status_label}</p>
                          </div>
                          {f.available ? (
                            <Button size="sm" onClick={() => downloadForm(f)} disabled={busy} data-testid={`download-form-${f.form_id}`}>
                              <Download className="w-4 h-4 mr-1" /> Download
                            </Button>
                          ) : (
                            <Badge variant="outline" className="text-xs">{f.status_label}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* CORRECTIONS */}
            <TabsContent value="corrections">
              <Corrections year={year} forms={data?.forms || []} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, tone = 'text-slate-900', testid }) => (
  <Card><CardContent className="p-4">
    <Icon className="w-4 h-4 text-slate-400 mb-2" />
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`text-lg font-bold ${tone}`} data-testid={testid}>{value}</p>
  </CardContent></Card>
);

function W9Form({ profile, onSaved }) {
  const [f, setF] = useState({
    legal_name: profile.legal_name || '', business_name: profile.business_name || '',
    tax_classification: profile.tax_classification || 'individual_sole_proprietor',
    llc_tax_class: profile.llc_tax_class || 'C',
    address1: profile.address1 || '', address2: profile.address2 || '',
    city: profile.city || '', state: profile.state || '', zip_code: profile.zip_code || '',
    tin_type: profile.tin_type || 'ssn', tin: '',
    exempt_payee_code: profile.exempt_payee_code || '',
    backup_withholding: profile.backup_withholding || false,
    certified: false, signature_name: profile.legal_name || '',
  });
  const [saving, setSaving] = useState(false);
  const [econsent, setEconsent] = useState(profile.econsent_given || false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.legal_name || !f.address1 || !f.city || !f.state || !f.zip_code) return toast.error('Please complete your name and address');
    if (!f.tin) return toast.error('Please enter your SSN, ITIN, or EIN');
    if (!f.certified) return toast.error('You must certify the W-9 to submit');
    if (!f.signature_name) return toast.error('Please type your name to sign');
    setSaving(true);
    try {
      await api.post('/tax-center/me/profile', f);
      toast.success('W-9 submitted securely');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit W-9');
    } finally { setSaving(false); }
  };

  const toggleConsent = async (v) => {
    setEconsent(v);
    try { await api.post('/tax-center/me/econsent', { consent: v }); toast.success(v ? 'Electronic delivery enabled' : 'Electronic delivery turned off'); }
    catch { toast.error('Could not update consent'); setEconsent(!v); }
  };

  return (
    <Card data-testid="w9-form">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border rounded-lg p-3">
          <Lock className="w-4 h-4 text-emerald-600" />
          Your taxpayer ID is encrypted and only used for tax administration. It is never shown on your profile or to clients.
        </div>
        {profile.has_w9 && (
          <p className="text-sm text-emerald-700" data-testid="w9-on-file">On file: {profile.tin_masked} · {CLASS_LABELS[profile.tax_classification] || profile.tax_classification}. Re-enter your TIN to update.</p>
        )}
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Legal name (as on tax return)" required><Input value={f.legal_name} onChange={(e) => set('legal_name', e.target.value)} data-testid="w9-legal-name" /></Field>
          <Field label="Business name (if different)"><Input value={f.business_name} onChange={(e) => set('business_name', e.target.value)} data-testid="w9-business-name" /></Field>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Federal tax classification" required>
            <Select value={f.tax_classification} onValueChange={(v) => set('tax_classification', v)}>
              <SelectTrigger data-testid="w9-classification"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CLASS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {f.tax_classification === 'llc' && (
            <Field label="LLC tax class">
              <Select value={f.llc_tax_class} onValueChange={(v) => set('llc_tax_class', v)}>
                <SelectTrigger data-testid="w9-llc-class"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="C">C — C corporation</SelectItem>
                  <SelectItem value="S">S — S corporation</SelectItem>
                  <SelectItem value="P">P — Partnership</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}
        </div>
        <Field label="Street address" required><Input value={f.address1} onChange={(e) => set('address1', e.target.value)} data-testid="w9-address1" /></Field>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Apt/Suite"><Input value={f.address2} onChange={(e) => set('address2', e.target.value)} /></Field>
          <Field label="City" required><Input value={f.city} onChange={(e) => set('city', e.target.value)} data-testid="w9-city" /></Field>
          <Field label="State" required><Input maxLength={2} value={f.state} onChange={(e) => set('state', e.target.value.toUpperCase())} data-testid="w9-state" /></Field>
          <Field label="ZIP" required><Input value={f.zip_code} onChange={(e) => set('zip_code', e.target.value)} data-testid="w9-zip" /></Field>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Taxpayer ID type" required>
            <Select value={f.tin_type} onValueChange={(v) => set('tin_type', v)}>
              <SelectTrigger data-testid="w9-tin-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ssn">SSN (Social Security Number)</SelectItem>
                <SelectItem value="itin">ITIN</SelectItem>
                <SelectItem value="ein">EIN (business)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={f.tin_type === 'ein' ? 'EIN' : 'SSN / ITIN'} required>
            <Input type="password" inputMode="numeric" placeholder="9 digits" value={f.tin} onChange={(e) => set('tin', e.target.value)} data-testid="w9-tin" />
          </Field>
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <Checkbox checked={f.backup_withholding} onCheckedChange={(v) => set('backup_withholding', !!v)} data-testid="w9-backup" />
          <span>I am subject to backup withholding (leave unchecked if the IRS has not notified you).</span>
        </label>
        <div className="border-t pt-4 space-y-3">
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <Checkbox checked={f.certified} onCheckedChange={(v) => set('certified', !!v)} data-testid="w9-certify" />
            <span>Under penalties of perjury, I certify the information above is correct and that I am a U.S. person (including a U.S. resident alien).</span>
          </label>
          <div className="grid md:grid-cols-2 gap-3 items-end">
            <Field label="Type your full name to sign" required><Input value={f.signature_name} onChange={(e) => set('signature_name', e.target.value)} data-testid="w9-signature" /></Field>
            <Button onClick={submit} disabled={saving} className="w-full md:w-auto" data-testid="w9-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1" />} Submit W-9 securely
            </Button>
          </div>
        </div>
        {profile.has_w9 && (
          <div className="border-t pt-4 flex items-center justify-between">
            <div className="text-sm">
              <p className="font-medium text-slate-900">Electronic delivery of tax forms</p>
              <p className="text-slate-500">Receive your 1099-NEC electronically in this Tax Center{profile.econsent_at ? ` (consented ${fmtDate(profile.econsent_at)})` : ''}.</p>
            </div>
            <Switch checked={econsent} onCheckedChange={toggleConsent} data-testid="econsent-switch" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Corrections({ year, forms }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: 'amount', description: '', form_id: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/tax-center/me/corrections'); setList(r.data.corrections || []); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.description) return toast.error('Please describe the correction');
    setSaving(true);
    try {
      await api.post('/tax-center/me/corrections', { tax_year: year, form_id: form.form_id || null, category: form.category, description: form.description });
      toast.success('Correction request submitted');
      setOpen(false); setForm({ category: 'amount', description: '', form_id: '' }); load();
    } catch { toast.error('Could not submit request'); }
    finally { setSaving(false); }
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Correction requests</h3>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" data-testid="new-correction-btn">Request a correction</Button></DialogTrigger>
            <DialogContent data-testid="correction-dialog">
              <DialogHeader><DialogTitle>Request a tax document correction</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Field label="What needs correcting?">
                  <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
                    <SelectTrigger data-testid="correction-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Name</SelectItem>
                      <SelectItem value="address">Address</SelectItem>
                      <SelectItem value="amount">Payment total (Box 1)</SelectItem>
                      <SelectItem value="tin">Taxpayer ID</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                {forms.length > 0 && (
                  <Field label="Related form (optional)">
                    <Select value={form.form_id} onValueChange={(v) => setForm((p) => ({ ...p, form_id: v }))}>
                      <SelectTrigger data-testid="correction-form"><SelectValue placeholder="Select a form" /></SelectTrigger>
                      <SelectContent>{forms.map((x) => <SelectItem key={x.form_id} value={x.form_id}>1099-NEC {x.tax_year}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                )}
                <Field label="Describe the issue">
                  <Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} data-testid="correction-description" />
                </Field>
              </div>
              <DialogFooter><Button onClick={submit} disabled={saving} data-testid="correction-submit">{saving ? 'Submitting…' : 'Submit request'}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {list.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">No correction requests.</p>
        ) : list.map((c) => (
          <div key={c.id} className="border rounded-lg p-3 text-sm" data-testid={`correction-${c.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium capitalize">{c.category} · {c.tax_year}</span>
              <Badge variant="outline" className="capitalize">{c.status.replace(/_/g, ' ')}</Badge>
            </div>
            <p className="text-slate-600 mt-1">{c.description}</p>
            {c.admin_notes && <p className="text-slate-500 mt-1">Admin: {c.admin_notes}</p>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const Field = ({ label, required, children }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
    {children}
  </div>
);
